/**
 * Tipos de dominio POS (venta en sucursal, borradores de cobro, advertencias de stock).
 * Los tipos que coinciden con el dominio de ventas se re-exportan desde el origen canónico.
 */
import type { FiscalMode, PaymentMethodRow, SaleItemInput, StockWarningDetail, StockWarningsResult } from '../../sales/infra/salesRepo';

export type PosFiscalMode = FiscalMode;
export type PosPaymentMethodRow = PaymentMethodRow;
export type PosSaleLineInput = SaleItemInput;
export type PosStockWarningDetail = StockWarningDetail;
export type PosStockWarningsResult = StockWarningsResult;

/** Medios de pago persistidos en SQLite (`payment_methods.id`). */
export type PosPaymentMethodId = 'cash' | 'transfer' | 'mercadopago_qr' | 'fiado' | 'card' | 'cheque';

/** Renglón de cobro en borrador (montos como string en UI). */
export type PosDraftPayment = {
  id: string;
  methodId: PosPaymentMethodId;
  amount: string;
  customerId?: string;
  /** Datos del cheque (methodId='cheque'). number es requerido server-side. */
  checkNumber?: string;
  checkDueDate?: string;
  checkDrawer?: string;
  checkBank?: string;
};

/** Cliente elegible para cobro en customer (vista mínima para POS). */
export type PosCheckoutCustomerOption = {
  id: string;
  name: string;
  phone: string | null;
  balance: number;
  creditLimit: number;
  blocked: boolean;
  priceListId?: string | null;
  /** CUIT del cliente (para auto-poblar comprador fiscal). */
  cuit?: string | null;
  /** Condición fiscal AFIP (5 = Consumidor Final, 1 = Responsable Inscripto, etc). */
  taxCondition?: number | null;
  /** Razón social / nombre fiscal. */
  businessName?: string | null;
};

/** Política de venta con stock insuficiente en POS (alineada al valor persistido en sucursal). */
export type PosStockSalePolicy = 'block' | 'warn' | 'allow';

export type PosStockNoticeVariant = PosStockSalePolicy;

/** Fila de grilla POS para un paquete de servicios (no es un Product del catálogo). */
export type PosPackageGridRow = {
  kind: 'package';
  /** id de public.service_packages — también viaja como productId de la línea. */
  packageId: string;
  name: string;
  price: number;
  sessionCount: number;
  validityDays: number;
};

/** Union de filas de la grilla POS: productos del catálogo o paquetes. */
export type PosCatalogRow = import('../../catalog/domain-api').ProductSearchRow | PosPackageGridRow;

/** Filtro de tipo de la grilla POS: catálogo de productos + 'package' (service_packages). */
export type PosCatalogItemTypeFilter =
  | Exclude<
      import('../../catalog/domain-api').CatalogItemTypeFilter,
      'combo' | 'recipe'
    >
  | 'package';

export function isPosPackageRow(row: PosCatalogRow): row is PosPackageGridRow {
  return (row as PosPackageGridRow).kind === 'package';
}
